import { Component, Output, EventEmitter, Input, computed, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { AuthSessionService } from '@core/auth/auth-session.service';
import { rolesIncludeSuperadmin } from '@core/auth/superadmin-access';
import { SignOutService } from '@core/auth/sign-out.service';
import { initialsFromSessionUser, primaryRoleLabel as formatPrimaryRole } from '@core/auth/user-display.utils';
import { OrderNotificationsService } from '@core/notifications/order-notifications.service';
import { formatOrderNotificationTime } from '@core/notifications/order-notification.model';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { BrandingComponent } from '../../vertical/sidebar/branding.component';
import { CompanySwitcherComponent } from '@core/shell/company-switcher/company-switcher.component';
import { LocationSwitcherComponent } from '@core/shell/location-switcher/location-switcher.component';
import { TenantCompanySwitchService } from '@core/tenant/tenant-company-switch.service';
import { TenantContextService } from '@core/tenant/tenant-context.service';
import { LocationContextService } from '@core/tenant/location-context.service';
import { openWorkspacePickerAndApply } from '@core/shell/workspace-picker-dialog/open-workspace-picker.util';
import { PermissionContextService } from '@core/auth/permission-context.service';
import { canOpenGemasPage } from '@core/auth/gemas-access.utils';
import { canManageTeamAccess } from '@core/auth/team-access';
import { resolveProfileConfigurationLink } from '@core/auth/profile-configuration-link.utils';
import { CompanyPlanContextService } from '@core/plan/company-plan-context.service';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import { tablerNavIconForState } from '@core/shell/nav-icon-weight.utils';
import { appBuildIdentity } from '@core/build-info';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';

interface OrderNotificationView {
  id: string;
  number: number;
  time: string;
}

interface profiledd {
  id: number;
  titleKey: string;
  icon: string;
  link?: string;
  /** Ruta actual coincide con el ítem (filled + accent). */
  active?: boolean;
  new?: boolean;
  logout?: boolean;
}

@Component({
  selector: 'app-horizontal-header',
  imports: [
    RouterModule,
    TablerIconsModule,
    MaterialModule,
    BrandingComponent,
    CompanySwitcherComponent,
    LocationSwitcherComponent,
    TranslateModule,
  ],
  templateUrl: './header.component.html',
})
export class AppHorizontalHeaderComponent {
  readonly authSession = inject(AuthSessionService);
  private readonly signOutService = inject(SignOutService);
  private readonly companySwitch = inject(TenantCompanySwitchService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly locationContext = inject(LocationContextService);
  private readonly router = inject(Router);
  private readonly permissionContext = inject(PermissionContextService);
  private readonly companyPlanContext = inject(CompanyPlanContextService);
  private readonly adminShellMode = inject(AdminShellModeService);
  private readonly dialog = inject(MatDialog);
  private readonly orderNotifications = inject(OrderNotificationsService);

  private readonly routerPath = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects.split('?')[0]),
      startWith(this.router.url.split('?')[0])
    ),
    { initialValue: this.router.url.split('?')[0] }
  );

  /** Versión de build (SemVer + SHA) para soporte — solo visible para superadmin. */
  readonly appVersionLabel = appBuildIdentity();
  readonly showAppVersion = computed(() => rolesIncludeSuperadmin(this.authSession.user()?.roles));

  readonly userInitialsDisplay = computed(() => {
    const u = this.authSession.user();
    return u ? initialsFromSessionUser(u) : '';
  });

  readonly sessionRoleBadge = computed(() => {
    const u = this.authSession.user();
    return u ? formatPrimaryRole(u) : null;
  });

  readonly showWorkspaceSwitcher = computed(() => this.companySwitch.showInTenantShell());

  readonly primaryCompanyDisplay = computed(() => {
    const name = this.companySwitch.activeCompany()?.name?.trim();
    return name && name.length > 0 ? name : null;
  });

  openWorkspacePicker(): void {
    openWorkspacePickerAndApply(this.dialog, {
      companies: this.companySwitch.accessibleCompanies(),
      activeCompanyId: this.companySwitch.activeCompanyId(),
      allowCreateBusiness: true,
      switchService: this.companySwitch,
      router: this.router,
      tenantContext: this.tenantContext,
      locationContext: this.locationContext,
    });
  }

  readonly toolbarUserLabel = computed(() => {
    const n = this.authSession.user()?.name?.trim();
    return n && n.length > 0 ? n : null;
  });

  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleMobileFilterNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();

  showFiller = false;

  signOut(): void {
    this.signOutService.signOut();
  }

  /** Pedidos nuevos recibidos por WebSocket (ver `OrderNotificationsService`). */
  readonly notificationsUnreadCount = this.orderNotifications.unreadCount;

  readonly orderNotificationItems = computed<OrderNotificationView[]>(() =>
    this.orderNotifications.items().map((item) => ({
      id: item.id,
      number: item.number,
      time: formatOrderNotificationTime(item.receivedAt),
    }))
  );

  /** Marca como leídas al abrir el menú (el badge no debe seguir sumando de fondo). */
  onNotificationsMenuOpened(): void {
    this.orderNotifications.markAllRead();
  }

  readonly profileMenuItems = computed(() => {
    this.permissionContext.loadState();
    this.companyPlanContext.loadState();
    this.companyPlanContext.summary();
    this.adminShellMode.mode();
    const path = this.routerPath();
    const can = this.permissionContext.can.bind(this.permissionContext);
    const preferPosConfig = this.adminShellMode.mode() === 'pos';
    const configurationLink = resolveProfileConfigurationLink(
      can,
      {
        moduleConfiguration: this.companyPlanContext.moduleConfigurationEnabled(),
        moduleUsers: this.companyPlanContext.moduleUsersEnabled(),
        rolesAdmin: this.companyPlanContext.canShowRolesAdminUi(),
      },
      { preferPosConfig }
    );

    const items: profiledd[] = [
      {
        id: 1,
        titleKey: 'header.profile.menu.myProfile',
        icon: tablerNavIconForState('user', isProfileMenuPathActive(path, '/profile')),
        link: '/profile',
        active: isProfileMenuPathActive(path, '/profile'),
      },
    ];

    if (canManageTeamAccess(this.authSession.user(), can)) {
      items.push({
        id: 5,
        titleKey: 'header.profile.menu.myPlan',
        icon: tablerNavIconForState('credit-card', isProfileMenuPathActive(path, '/account/plan')),
        link: '/account/plan',
        active: isProfileMenuPathActive(path, '/account/plan'),
      });
    }

    if (canOpenGemasPage(can)) {
      items.push({
        id: 3,
        titleKey: 'header.profile.menu.gemas',
        icon: tablerNavIconForState('diamond', isProfileMenuPathActive(path, '/account/gemas')),
        link: '/account/gemas',
        active: isProfileMenuPathActive(path, '/account/gemas'),
      });
      items.push({
        id: 4,
        titleKey: 'header.profile.menu.academia',
        icon: tablerNavIconForState('book', isProfileMenuPathActive(path, '/account/academia')),
        link: '/account/academia',
        active: isProfileMenuPathActive(path, '/account/academia'),
      });
    }

    if (configurationLink) {
      items.push({
        id: 2,
        titleKey: 'header.profile.menu.configuration',
        icon: tablerNavIconForState(
          'settings',
          isProfileMenuPathActive(path, configurationLink)
        ),
        link: configurationLink,
        active: isProfileMenuPathActive(path, configurationLink),
      });
    }

    return items;
  });
}

function isProfileMenuPathActive(path: string, link: string): boolean {
  return path === link || path.startsWith(`${link}/`);
}
